/**
 * Hierarchy Selector Component
 *
 * Purpose: Form display for hierarchical field with selected values as chips
 * Architecture: Autocomplete-style selector with floating label and chip display
 * Author: Development Team
 * Created: 2025-01-21
 *
 * Features:
 * - Floating label animation (Material-UI style)
 * - Selected values displayed as chips
 * - Click to open hierarchy dialog
 * - Chip removal for individual values
 * - Error state display
 * - Disabled and read-only states
 */

import React from 'react';
import { Box, FormControl, InputLabel, Typography } from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import type { MasterDataValue } from '../../types/mdm';
import { ValueChipColorKey } from '../../theme/orchestrateTheme';
import ValueChip from '../common/ValueChip';
import { ALL_VALUE } from '../../utils/listValues';

/**
 * Props interface for HierarchySelector
 */
export interface HierarchySelectorProps {
  /**
   * Field label
   */
  label: string;

  /**
   * Selected master data values
   */
  selectedValues: MasterDataValue[];

  /**
   * Whether field is required
   */
  isRequired: boolean;

  /**
   * Whether field is disabled
   */
  disabled?: boolean;

  /**
   * Whether field is read-only
   */
  readOnly?: boolean;

  /**
   * Whether field has been touched
   */
  touched?: boolean;

  /**
   * Error message
   */
  error?: string;

  /**
   * Whether field is focused
   */
  isFocused: boolean;

  /**
   * Focus handler
   */
  onFocus: () => void;

  /**
   * Blur handler
   */
  onBlur: () => void;

  /**
   * Click handler to open dialog
   */
  onClick: () => void;

  /**
   * Remove value handler
   */
  onRemove: (valueId: string) => void;

  /** ORCH-400: values shown without a delete icon (outside this user's access) */
  lockedIds?: ReadonlySet<string>;

  /**
   * Help text shown under the field when there is no validation error
   */
  helperText?: React.ReactNode;

  /**
   * Test ID for testing
   */
  dataTestId?: string;

  /**
   * MUI theme token for the border color driven by the field indicator system
   * (e.g. 'error.main', 'success.main'). Applied when the field is not focused
   * and has no validation error. Allows the maestro/submitted indicator state to
   * reach this custom component, which does not use MuiOutlinedInput.
   */
  indicatorBorderColor?: string;
}

/**
 * Hierarchy Selector Component
 *
 * Purpose: Display selected hierarchical values with autocomplete-style UI
 * Logic:
 * - Shows floating label when focused or has values
 * - Displays selected values as removable chips
 * - Opens dialog on click
 * - Shows error state when touched and has error
 *
 * Input: HierarchySelectorProps
 * Output: JSX.Element
 *
 * Example:
 * ```tsx
 * <HierarchySelector
 *   label="Country"
 *   selectedValues={selectedValues}
 *   onClick={handleOpen}
 *   onRemove={handleRemove}
 * />
 * ```
 */
export const HierarchySelector: React.FC<HierarchySelectorProps> = ({
  label,
  selectedValues,
  isRequired,
  disabled = false,
  readOnly = false,
  touched = false,
  error,
  helperText,
  isFocused,
  onFocus,
  onBlur,
  onClick,
  onRemove,
  lockedIds,
  dataTestId = 'hierarchy-selector',
  indicatorBorderColor,
}) => {
  // Determine if label should be shrunk (floating up)
  const shouldShrinkLabel = isFocused || selectedValues.length > 0;

  return (
    <Box data-testid={dataTestId}>
      {/* Autocomplete-style selector with floating label */}
      <FormControl
        variant="outlined"
        fullWidth
        error={touched && !!error}
        disabled={disabled}
        required={isRequired}
      >
        <InputLabel
          shrink={shouldShrinkLabel}
          sx={{
            backgroundColor: 'background.paper',
            paddingX: 0.5,
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)',
            },
          }}
        >
          {label}
        </InputLabel>
        <Box
          onClick={() => {
            if (!disabled && !readOnly) {
              onClick();
            }
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          tabIndex={disabled || readOnly ? -1 : 0}
          sx={{
            position: 'relative',
            border: '1px solid',
            borderColor: isFocused
              ? touched && !!error
                ? 'error.main'
                : 'primary.main'
              : touched && !!error
                ? 'error.main'
                : indicatorBorderColor || 'rgba(0, 0, 0, 0.23)',
            borderRadius: (theme) => `${theme.shape.borderRadius}px`,
            cursor: disabled || readOnly ? 'default' : 'pointer',
            backgroundColor: disabled ? 'action.disabledBackground' : 'background.paper',
            transition: 'border-color 0.2s, border-width 0.2s',
            '&:hover': {
              borderColor:
                disabled || readOnly
                  ? 'rgba(0, 0, 0, 0.23)'
                  : isFocused
                    ? touched && !!error
                      ? 'error.main'
                      : 'primary.main'
                    : touched && !!error
                      ? 'error.main'
                      : indicatorBorderColor || 'text.primary',
            },
            ...(isFocused && {
              borderWidth: '2px',
            }),
            '&:focus': {
              outline: 'none',
            },
          }}
        >
          {/* Inner container with padding - matches MUI Autocomplete structure */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.5,
              padding: '7px 9px',
              minHeight: '38px',
              boxSizing: 'border-box',
              ...(isFocused && {
                padding: '6px 8px',
              }),
            }}
          >
            {/* Selected values as chips */}
            {selectedValues.length > 0 &&
              selectedValues.map((val) => {
                const chipColorKey = val.chip_color as ValueChipColorKey | null | undefined;

                return (
                  <ValueChip
                    key={val.id}
                    label={val.name}
                    chipColor={chipColorKey}
                    size="small"
                    onDelete={
                      disabled || readOnly || lockedIds?.has(val.id)
                        ? undefined
                        : () => {
                            onRemove(val.id);
                          }
                    }
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation(); // Prevent opening dialog when clicking chip
                    }}
                    sx={{ maxWidth: '100%' }}
                    // ORCH-418: a value of ALL shows as one "All" chip
                    data-testid={`${dataTestId}-chip-${val.id === ALL_VALUE ? 'all' : val.id}`}
                  />
                );
              })}

            {/* Dropdown arrow */}
            <ArrowDropDownIcon
              sx={{
                color: disabled ? 'action.disabled' : 'action.active',
                marginLeft: 'auto',
                flexShrink: 0,
              }}
              data-testid={`${dataTestId}-arrow`}
            />
          </Box>
        </Box>

        {/* Error text */}
        {touched && error && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 0.5, display: 'block', ml: 1.75 }}
            data-testid={`${dataTestId}-error`}
          >
            {error}
          </Typography>
        )}
        {helperText && !(touched && error) && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: 'block', ml: 1.75 }}
            data-testid={`${dataTestId}-helper`}
          >
            {helperText}
          </Typography>
        )}
      </FormControl>
    </Box>
  );
};

export default HierarchySelector;
